import {LanguageDefinition, Token} from '../types';
import {normalizeFanucTpForDiff} from './fanuc-tp-normalize';

/**
 * FANUC TP言語のキーワード定義
 */
const FANUC_TP_KEYWORDS = {
  motion: ['J', 'L', 'C', 'A'],
  control: [
    'CALL',
    'RUN',
    'END',
    'ABORT',
    'PAUSE',
    'RESUME',
    'STOP',
    'WAIT',
    'SKIP',
    'JMP',
    'LBL',
    'RSR',
    'MACRO'
  ],
  welding: ['ARC', 'ARCOF', 'WELD', 'WEAVE', 'TOUCHUP', 'TRACK'],
  condition: ['IF', 'ELSE', 'ENDIF', 'SELECT', 'FOR', 'ENDFOR', 'WHILE', 'ENDWHILE'],
  io: [
    'DO',
    'DI',
    'RO',
    'RI',
    'GO',
    'GI',
    'AO',
    'AI',
    'SO',
    'SI',
    'UO',
    'UI',
    'WO',
    'WI',
    'PULSE',
    'TOGGLE'
  ],
  operation: [
    'ADD',
    'SUB',
    'MULT',
    'DIV',
    'MOD',
    'AND',
    'OR',
    'NOT',
    'INC',
    'DEC',
    'CLR',
    'CNT',
    'UTOOL',
    'UFRAME',
    'PAYLOAD',
    'OVERRIDE',
    'LOCK',
    'UNLOCK'
  ],
  keyword: [
    'ON',
    'OFF',
    'FINE',
    'CNT',
    'CR',
    'CD',
    'COORD',
    'INC',
    'ACC',
    'RT',
    'AP_LD',
    'LPOS',
    'JPOS',
    'UFRAME',
    'UTOOL',
    'PR',
    'R',
    'P',
    'TRUE',
    'FALSE',
    'ENABLE',
    'DISABLE'
  ]
};

/**
 * FANUC TP言語の行をトークンに分割する
 */
const tokenizeLine = (line: string): Token[] => {
  const tokens: Token[] = [];
  const trimmedLine = line.trim();

  // 空行
  if (trimmedLine.length === 0) {
    tokens.push({type: 'text', value: line});
    return tokens;
  }

  // コメント行（!で始まる or --で始まる）
  if (trimmedLine.startsWith('!') || trimmedLine.startsWith('--')) {
    tokens.push({type: 'comment', value: line});
    return tokens;
  }

  // ディレクティブ（/で始まる行: /PROG, /ATTR, /MN など）
  if (trimmedLine.startsWith('/')) {
    tokens.push({type: 'directive', value: line});
    return tokens;
  }

  // 先頭の空白を保持
  const leadingSpaces = line.match(/^(\s*)/)?.[1] || '';
  if (leadingSpaces) {
    tokens.push({type: 'text', value: leadingSpaces});
  }

  const codePart = line.substring(leadingSpaces.length);

  // 行番号付き行（1: J P[1] ...）
  const lineNumMatch = codePart.match(/^(\d+)(:)(\s*)/);
  if (lineNumMatch) {
    tokens.push({type: 'label', value: lineNumMatch[1]});
    tokens.push({type: 'text', value: lineNumMatch[2]});
    if (lineNumMatch[3]) {
      tokens.push({type: 'text', value: lineNumMatch[3]});
    }
  }

  const afterLineNum = lineNumMatch ? codePart.substring(lineNumMatch[0].length) : codePart;

  // トークンパターン
  const tokenPattern =
    /(\[.*?\]|'[^']*'|"[^"]*"|PR\[\d+\]|R\[\d+\]|P\[\d+\]|DO\[\d+\]|DI\[\d+\]|RO\[\d+\]|RI\[\d+\]|GO\[\d+\]|GI\[\d+\]|AO\[\d+\]|AI\[\d+\]|SO\[\d+\]|SI\[\d+\]|UO\[\d+\]|UI\[\d+\]|LBL\[\d+\]|[A-Z_][A-Z0-9_]*|[+-]?\d+\.?\d*|[=(),;:+\-*\/%]+|\s+)/gi;

  let lastIndex = 0;
  let match;

  while ((match = tokenPattern.exec(afterLineNum)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({type: 'text', value: afterLineNum.substring(lastIndex, match.index)});
    }

    const word = match[0];
    const upperWord = word.toUpperCase();

    if (/^\s+$/.test(word)) {
      tokens.push({type: 'text', value: word});
    } else if (/^[=(),;:+\-*\/%]+$/.test(word)) {
      tokens.push({type: 'text', value: word});
    } else if (/^'[^']*'$/.test(word) || /^"[^"]*"$/.test(word)) {
      tokens.push({type: 'string', value: word});
    } else if (/^\[.*\]$/.test(word)) {
      tokens.push({type: 'variable', value: word});
    } else if (/^(PR|R|P|DO|DI|RO|RI|GO|GI|AO|AI|SO|SI|UO|UI|LBL)\[\d+\]$/i.test(word)) {
      tokens.push({type: 'variable', value: word});
    } else if (FANUC_TP_KEYWORDS.motion.includes(upperWord)) {
      tokens.push({type: 'motion', value: word});
    } else if (FANUC_TP_KEYWORDS.control.includes(upperWord)) {
      tokens.push({type: 'control', value: word});
    } else if (FANUC_TP_KEYWORDS.welding.includes(upperWord)) {
      tokens.push({type: 'welding', value: word});
    } else if (FANUC_TP_KEYWORDS.condition.includes(upperWord)) {
      tokens.push({type: 'condition', value: word});
    } else if (FANUC_TP_KEYWORDS.io.includes(upperWord)) {
      tokens.push({type: 'io', value: word});
    } else if (FANUC_TP_KEYWORDS.operation.includes(upperWord)) {
      tokens.push({type: 'operation', value: word});
    } else if (FANUC_TP_KEYWORDS.keyword.includes(upperWord)) {
      tokens.push({type: 'keyword', value: word});
    } else if (/^[+-]?\d+\.?\d*$/.test(word)) {
      tokens.push({type: 'number', value: word});
    } else {
      tokens.push({type: 'text', value: word});
    }

    lastIndex = match.index + word.length;
  }

  if (lastIndex < afterLineNum.length) {
    tokens.push({type: 'text', value: afterLineNum.substring(lastIndex)});
  }

  return tokens;
};

/**
 * FANUC TP言語定義
 */
export const fanucTpLanguage: LanguageDefinition = {
  name: 'FANUC TP',
  extensions: ['.ls', '.tp'],
  badge: 'FANUC',
  tokenizeLine,
  normalizeForDiff: normalizeFanucTpForDiff,
  supportsVolatileState: true
};
